/**
 * API client for deal_exit_leverage (Contract Intelligence Exit Leverage signals).
 * Backend endpoint: POST /api/exit-leverage with body { deal_ids: [...] }
 * Returns { records: { [deal_id]: row } }
 */

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? (import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000')
    : 'https://api.neogtm.com';

/**
 * Fetch exit leverage records for multiple deal IDs.
 * Uses POST to avoid URL length limits with hundreds of IDs.
 * @param {string} tenantId
 * @param {string[]} dealIds
 * @returns {Promise<Record<string, ExitLeverageRecord>>} Map of deal_id -> record
 */
export async function fetchDealExitLeverage(tenantId, dealIds) {
  if (!dealIds?.length) return {};
  const ids = [...new Set(dealIds)].filter(Boolean).slice(0, 500);
  if (ids.length === 0) return {};

  const res = await fetch(`${API_BASE_URL}/api/exit-leverage`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ deal_ids: ids }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 404) return {};
    throw new Error(`Exit leverage failed (${res.status}): ${text}`);
  }
  const data = await res.json();
  const raw = data.records ?? data ?? {};
  if (Array.isArray(raw)) {
    const map = {};
    raw.forEach((r) => {
      const k = r.deal_id ?? r.id;
      if (k) map[k] = r;
    });
    return map;
  }
  return raw;
}
